import { Link, createFileRoute, type ErrorComponentProps } from '@tanstack/react-router'
import { useMemo, useRef, useState, type ReactNode } from 'react'
import { z } from 'zod'

import RouteErrorPage from '@/components/layout/RouteErrorPage'
import ScreenLayout from '@/components/layout/ScreenLayout'
import Button from '@/components/ui/Button'
import EmptyState from '@/components/ui/EmptyState'
import { stateActionClass } from '@/components/ui/empty-state-variants'
import Skeleton from '@/components/ui/Skeleton'
import { toast } from '@/components/ui/toast-store'
import CoverPage from '@/features/newsletter/components/CoverPage'
import { IssueBackLink } from '@/features/newsletter/components/IssueNavButtons'
import IssueContents from '@/features/newsletter/components/IssueContents'
import IssuePagePill from '@/features/newsletter/components/IssuePagePill'
import { IssueReaderContext } from '@/features/newsletter/components/issue-reader-context'
import { useIssueScroll } from '@/features/newsletter/components/use-issue-scroll'
import MarketUpdatesCard from '@/features/market-rail/components/MarketUpdatesCard'
import PopularStocksCard from '@/features/market-rail/components/PopularStocksCard'
import SectionPages from '@/features/newsletter/components/SectionPages'
import { useSuspenseNewsletterIssue } from '@/features/newsletter/api/queries'
import {
  COVER_ANCHOR_ID,
  contentsRows,
  pageAnchors,
  planIssuePages,
} from '@/features/newsletter/components/issue-pages'
import { formatIssueDay } from '@/features/newsletter/components/issue-line'
import { cn } from '@/lib/utils'
import type { NewsletterIssue } from '@/schemas/newsletter'
import { loadNewsletterIssue } from '@/screens/newsletter/NewsletterIssueScreen.loader'
import { parseIssueNumber } from '@/screens/newsletter/lib/parseIssueNumber'

/**
 * "There is no such issue." — declared before `Route` so `NewsletterIssueRoute`
 * and `NewsletterIssueScreen` below can both reach it: a bad param never
 * reaches the network (see `parseIssueNumber`) and lands here directly,
 * without the suspense-reading screen ever mounting at all.
 *
 * NOT a sign-in prompt, and never one. All three newsletter routes are
 * `V5PublicAPIView` — `errors.ts` refuses to emit a `signInRequired` branch
 * for exactly this reason — so the only thing a `NEWSLETTER_NOT_FOUND` can
 * mean is that the number in the URL names nothing readable: an issue that
 * does not exist, one still in draft, or a param that was never an issue
 * number at all. The backend deliberately refuses to tell those apart, and
 * so does this screen. The way out is the archive, not a session.
 */
function IssueNotFound({ description }: { description: string }) {
  return (
    <EmptyState
      kind="not-found"
      size="page"
      headingLevel="h1"
      title="Issue Not Found"
      description={description}
      action={
        <Link to="/market/newsletters" className={stateActionClass('page')}>
          Browse all issues
        </Link>
      }
    />
  )
}

/**
 * Keys the screen by the raw param so navigating issue 20 → 19 remounts it
 * rather than re-rendering it in place. The router's `defaultRemountDeps`
 * (app/router.tsx) remounts on it too; the key keeps the screen from
 * depending on that, since any local state it seeds once from the route
 * would otherwise keep naming something from the OLD issue. A remount
 * re-seeds every piece of local state for free, without an effect chasing
 * the param.
 *
 * A param that fails `parseIssueNumber` is filtered out HERE, before the
 * suspense-reading screen ever mounts: `useSuspenseNewsletterIssue` cannot
 * take `enabled: false` (suspense hooks refuse it), so the only way to skip
 * the read for an unparseable param is to never call the hook at all. The
 * loader (`NewsletterIssueScreen.loader.ts`) makes the identical check
 * before ever asking the network.
 */
function NewsletterIssueRoute() {
  const { issueNumber: raw } = Route.useParams()
  const issueNumber = parseIssueNumber(raw)
  if (issueNumber === null) {
    return (
      <ScreenLayout pageTitle="Issue Not Found">
        <IssueNotFound description="That address does not name an issue of MoneyMitra Weekly." />
      </ScreenLayout>
    )
  }
  return <NewsletterIssueScreen key={issueNumber} issueNumber={issueNumber} />
}

/**
 * The page before the issue has loaded — the same header-row-plus-
 * `ReaderSkeleton` shape the screen's own loading branch used to draw. Reads
 * `Route.useParams()` for the real issue number, same as `StockPending` does
 * for its ticker; an unparseable param never gets here at all (see
 * `NewsletterIssueRoute`'s own doc) so `issueNumber` is always a positive
 * integer.
 */
function IssuePending() {
  const { issueNumber: raw } = Route.useParams()
  const issueNumber = parseIssueNumber(raw) ?? 0
  return (
    <ScreenLayout
      pending
      pendingLabel="Loading this issue…"
      header={<IssueHeaderRow metaLine={<Skeleton className="h-3 w-56" />} />}
      pageTitle={`Issue ${String(issueNumber)}`}
    >
      <ReaderSkeleton />
    </ScreenLayout>
  )
}

/**
 * The copy this screen used for a genuine failure before it had a loader.
 * NOT drawn for `NEWSLETTER_NOT_FOUND` — that resolves to `null` rather than
 * throwing (`newsletterIssueQueryOptions`'s own doc), so it never reaches
 * here; only a real failure (a 5xx, a transport error) does.
 *
 * Carries the same `IssueHeaderRow` `IssuePending` shows
 * (docs/route-loading.md, "Error components": a route whose pending page has a
 * header gives its error page the same one, so the frame does not change shape
 * between the two) — no meta line, since there is no issue to summarise and a
 * skeleton bar would claim one is still coming.
 */
function IssueError(props: ErrorComponentProps) {
  return (
    <RouteErrorPage
      {...props}
      header={<IssueHeaderRow metaLine={null} />}
      title="Couldn't load this issue"
      description="Check your internet and try again."
    />
  )
}

/**
 * `?page=` is the page in view (the cover is 1), written back as the member scrolls and read once on
 * arrival so a shared link opens where it was copied. Anything unreadable falls back to the cover.
 */
const issueSearchSchema = z.object({
  page: z.coerce.number().int().min(1).optional().catch(undefined),
})

export const Route = createFileRoute('/_app/market/newsletters/$issueNumber')({
  validateSearch: issueSearchSchema,
  loader: loadNewsletterIssue,
  pendingComponent: IssuePending,
  errorComponent: IssueError,
  component: NewsletterIssueRoute,
})

/**
 * The cover-plus-page shapes, sized like the real thing: the cover at its fixed 452px and a second
 * page under it. No `role="status"`/`aria-busy` of its own — `ScreenLayout`'s `pending` already
 * carries the one announcement for the whole page region (docs/route-loading.md, "Skeletons").
 */
function ReaderSkeleton() {
  return (
    <div className="flex w-full flex-col gap-6">
      <div className="overflow-hidden bg-card">
        <Skeleton className="h-113 w-full rounded-none" />
        <div className="flex flex-col gap-3 px-8 py-6">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-8 w-3/4" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-3 w-32" />
        </div>
      </div>
      <div className="flex flex-col gap-4 bg-card px-10 py-6">
        <Skeleton className="h-3 w-28" />
        <Skeleton className="h-7 w-52" />
        <Skeleton className="h-54 w-full" />
      </div>
    </div>
  )
}

/** How long "Link copied" stays on the Share button. */
const COPIED_MS = 1600

/**
 * The line under the strip that opens the reader (the mock's `nl-rhead`): the back arrow, the
 * "MoneyMitra Weekly, 13 Sept 2026, 6 min read" line, and Share. The issue's title is the page's
 * `h1` inside the cover, and "Newsletters" is lit in the strip above, so there is no title here.
 * `metaLine` is a slot so the skeleton can hand it a bar before the issue has loaded.
 *
 * Share copies the address and says so in its own label for a moment (gold, as the mock does); a
 * refused copy is a toast. Nothing in the contract names a share endpoint, so this is the whole
 * behaviour.
 */
function IssueHeaderRow({ metaLine }: { metaLine: ReactNode }) {
  const [copied, setCopied] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  const handleShare = () => {
    // `navigator.clipboard` is only defined in a secure context with a supporting browser; reading
    // `.writeText` off `undefined` throws synchronously, so the `try` is what survives that.
    try {
      void navigator.clipboard.writeText(window.location.href).then(
        () => {
          setCopied(true)
          clearTimeout(timer.current)
          timer.current = setTimeout(() => {
            setCopied(false)
          }, COPIED_MS)
        },
        () => {
          toast.error("Couldn't copy the link")
        },
      )
    } catch {
      toast.error("Couldn't copy the link")
    }
  }

  return (
    <div className="flex items-start gap-4">
      <div className="flex min-w-0 flex-1 items-center gap-4 pt-1">
        <IssueBackLink to="/market/newsletters" />
        <div className="min-w-0 text-body-m text-muted-foreground-subtle">{metaLine}</div>
      </div>
      <Button
        variant="secondary"
        className={cn('w-30 shrink-0', copied && 'text-tone-accent')}
        onClick={handleShare}
      >
        {copied ? 'Link copied' : 'Share'}
      </Button>
      <span role="status" className="sr-only">
        {copied ? 'Link copied' : null}
      </span>
    </div>
  )
}

/**
 * Newsletter · Issue N — the reader. `AppShell` supplies the chrome; this is the page.
 *
 * The cover first, then one card per page the sections open, 24 apart. Three things tie them
 * together, all from `useIssueScroll`: ONE sticky pill carrying the number of the page in view, an
 * "In this issue" list at the head of the rail (rows scroll to their page), and a pager in every
 * page's foot — with ← and → turning pages and `?page=` following along (`COMPARE-nw` §5).
 *
 * The `NEWSLETTER_NOT_FOUND` branch is a NOT-FOUND screen, not the failure card: the loader resolves
 * that one code to `data: null` (`newsletterIssueQueryOptions`), so a plain `null` check tells it
 * apart from a genuine failure, which is the route's own `IssueError`. A bad param never reaches the
 * network (`parseIssueNumber`).
 */
function NewsletterIssueScreen({ issueNumber }: { issueNumber: number }) {
  const { data: issue } = useSuspenseNewsletterIssue(issueNumber)
  const { page: pageParam } = Route.useSearch()

  if (issue === null) {
    return (
      <ScreenLayout pageTitle="Issue Not Found">
        <IssueNotFound description="We could not find that issue of MoneyMitra Weekly." />
      </ScreenLayout>
    )
  }
  return <IssueReader issue={issue} initialPage={pageParam ?? 1} />
}

function IssueReader({ issue, initialPage }: { issue: NewsletterIssue; initialPage: number }) {
  // Derived once so a page that shares its number with the block before it (a `did_you_know`
  // folded into the preceding `market_events`) can never disagree with its own footer.
  const placements = useMemo(() => planIssuePages(issue.sections), [issue.sections])
  const anchors = useMemo(() => pageAnchors(placements), [placements])
  const rows = useMemo(() => contentsRows(placements), [placements])
  const reader = useIssueScroll(anchors, initialPage)

  const day = formatIssueDay(issue.publishedAt)
  const readTime =
    issue.readTimeMinutes === null ? null : `${String(issue.readTimeMinutes)} min read`
  const metaLine = (
    <p className="truncate">
      {[issue.source, day, readTime].filter((part): part is string => part !== null).join(', ')}
    </p>
  )

  return (
    <IssueReaderContext value={reader}>
      <ScreenLayout
        header={<IssueHeaderRow metaLine={metaLine} />}
        pageTitle={`Issue ${String(issue.issueNumber)}`}
        // The rail leads with the issue's contents, then the shared Market news and Popular stocks.
        rail={
          <div className="flex flex-col gap-10">
            <IssueContents rows={rows} />
            <MarketUpdatesCard />
            <PopularStocksCard />
          </div>
        }
      >
        <div className="flex flex-col gap-6">
          <IssuePagePill page={reader.page} />
          <div id={COVER_ANCHOR_ID} className="scroll-mt-[calc(var(--header-h)+var(--space-4))]">
            <CoverPage issue={issue} />
          </div>
          <SectionPages
            source={issue.source}
            issueNumber={issue.issueNumber}
            publishedAt={issue.publishedAt}
            placements={placements}
          />
        </div>
      </ScreenLayout>
    </IssueReaderContext>
  )
}
